import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./TaskResourceAllocationManager.module.scss";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import searchService from "../../store/services/search.service";
import scopeService from "../../store/services/scope.service";
import { FilterCross, AddPersonIcon } from "../../modules/default-svg";
import {
    TeachingPopover, Avatar, Button, TeachingPopoverBody,
    TeachingPopoverSurface, TeachingPopoverTrigger,
    Skeleton, SkeletonItem, Tooltip, Switch,
} from "@fluentui/react-components";
import Input from "../controls/input/customInput/CustomInput";
import Spin from "../controls/spinner/Spin";
import SafeHtml from "../safeHtml/SafeHtml";
import { ChevronDown, ChevronUp, Copy, Clock, Calendar, Check, X, AlertTriangle, XCircle } from "lucide-react";
import moment from "moment";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_HPD = 24;
const DEFAULT_HPD = 0;
const DEBOUNCE_MS = 400;
const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const roundTotal = (n) => {
    const r = round2(n);
    const nearest = Math.round(r);
    return Math.abs(r - nearest) <= 0.1 ? nearest : r;
};
const todayISO = (includeWeekends = true) => {
    const d = moment();
    if (!includeWeekends) {
        const dow = d.day();
        if (dow === 6) d.add(2, "days");      // Saturday → Monday
        else if (dow === 0) d.add(1, "days");  // Sunday  → Monday
    }
    return d.format("YYYY-MM-DD");
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

function countDays(startDate, endDate, includeWeekends = false) {
    if (!startDate || !endDate) return 0;
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");
    if (end.isBefore(start)) return 0;
    if (includeWeekends) return end.diff(start, "days") + 1;
    let count = 0;
    const cur = start.clone();
    while (cur.isSameOrBefore(end, "day")) {
        const dow = cur.day();
        if (dow !== 0 && dow !== 6) count++;
        cur.add(1, "day");
    }
    return count;
}

function addWorkingDays(startDate, days, includeWeekends = false) {
    if (!startDate || days <= 0) return startDate;
    const cur = moment(startDate, "YYYY-MM-DD");
    let remaining = days - 1;
    while (remaining > 0) {
        cur.add(1, "day");
        const dow = cur.day();
        if (includeWeekends || (dow !== 0 && dow !== 6)) remaining--;
    }
    return cur.format("YYYY-MM-DD");
}

// Snap weekend dates to nearest weekday (start → next Mon, end → prev Fri)
function snapToWeekday(dateStr, direction = "forward") {
    if (!dateStr) return dateStr;
    const d = moment(dateStr, "YYYY-MM-DD");
    const dow = d.day();
    if (dow === 0 || dow === 6) {
        if (direction === "forward") {
            // Saturday → +2, Sunday → +1
            d.add(dow === 6 ? 2 : 1, "days");
        } else {
            // Saturday → -1, Sunday → -2
            d.subtract(dow === 6 ? 1 : 2, "days");
        }
    }
    return d.format("YYYY-MM-DD");
}

// ─── Recalc (pure function) ───────────────────────────────────────────────────
//
// Invariants (no circular reference):
//   - Each branch handles ONE changed field and returns early.
//   - "totalHours" is the anchor when the user enters it — hpd adjusts to fit.
//
// Use-cases for totalHours change:
//   1. hpd > 0, dates exist          → keep totalHours & dates, adjust hpd
//   2. hpd > 0, no dates             → derive dates from totalHours/hpd, adjust hpd to exact fit
//   3. hpd === 0, dates exist         → distribute totalHours ÷ days → hpd (cap 24h, extend if needed)
//   4. hpd === 0, no dates            → store totalHours, wait for hpd
//
// Use-cases for hoursPerDay change:
//   - hpd > 0, dates exist           → recalc totalHours = hpd × days
//   - hpd > 0, totalHours > 0        → derive dates from totalHours / hpd
//
// Use-cases for startDate / endDate:
//   - hpd > 0, both dates            → recalc totalHours
//   - hpd === 0, totalHours > 0, both dates → distribute → derive hpd
//
// "today" always snaps to next working day when weekends are excluded.
//

function recalc(patch, current) {
    const next = { ...current, ...patch };
    const iw = next.isIncludeWeekends ?? false;

    // Clamp hpd
    if (next.hoursPerDay !== null && next.hoursPerDay !== undefined) {
        next.hoursPerDay = Math.min(Math.max(parseFloat(next.hoursPerDay) || 0, 0), MAX_HPD);
    }

    const hpd = next.hoursPerDay ?? 0;

    // When weekends are excluded, snap dates so they don't land on weekends
    if (!iw) {
        if ("startDate" in patch && next.startDate) {
            next.startDate = snapToWeekday(next.startDate, "forward");
        }
        if ("endDate" in patch && next.endDate) {
            next.endDate = snapToWeekday(next.endDate, "backward");
        }
    }

    // ── hoursPerDay changed ───────────────────────────────────────────────
    if ("hoursPerDay" in patch) {
        if (hpd === 0) {
            next.totalHours = 0;
            next.startDate = null;
            next.endDate = null;
            return next;
        }
        if (next.startDate && next.endDate) {
            next.totalHours = roundTotal(hpd * countDays(next.startDate, next.endDate, iw));
        } else if ((next.totalHours ?? 0) > 0) {
            // totalHours was waiting for hpd → now derive dates
            const start = next.startDate || todayISO(iw);
            next.startDate = start;
            const days = Math.ceil(next.totalHours / hpd);
            next.endDate = addWorkingDays(start, days, iw);
            next.totalHours = roundTotal(hpd * countDays(next.startDate, next.endDate, iw));
        }
        return next;
    }

    // ── totalHours changed ────────────────────────────────────────────────
    if ("totalHours" in patch) {
        const total = parseFloat(next.totalHours) || 0;

        if (total === 0) {
            next.totalHours = 0;
            return next;
        }

        // Case 1: hpd provided → derive dates, totalHours stays as anchor
        if (hpd > 0) {
            if (next.startDate && next.endDate) {
                // Both dates exist → keep dates & totalHours, adjust hpd
                const days = countDays(next.startDate, next.endDate, iw);
                if (days > 0) {
                    let newHpd = round2(total / days);
                    if (newHpd > MAX_HPD) {
                        newHpd = MAX_HPD;
                        const neededDays = Math.ceil(total / MAX_HPD);
                        next.endDate = addWorkingDays(next.startDate, neededDays, iw);
                    }
                    next.hoursPerDay = newHpd;
                }
                next.totalHours = total;
            } else {
                // No dates (or only start) → derive dates from hpd
                const start = next.startDate || todayISO(iw);
                next.startDate = start;
                const days = Math.ceil(total / hpd);
                next.endDate = addWorkingDays(start, days, iw);
                // Adjust hpd to fit totalHours exactly in the derived range
                const actualDays = countDays(next.startDate, next.endDate, iw);
                next.hoursPerDay = actualDays > 0 ? round2(total / actualDays) : hpd;
                next.totalHours = total;
            }
            return next;
        }

        // Case 2: hpd === 0 and dates are provided → distribute to get hpd
        if (next.startDate && next.endDate) {
            const days = countDays(next.startDate, next.endDate, iw);
            if (days > 0) {
                let derivedHpd = round2(total / days);
                if (derivedHpd > MAX_HPD) {
                    // Cap at 24h, extend end date, keep totalHours
                    derivedHpd = MAX_HPD;
                    const neededDays = Math.ceil(total / MAX_HPD);
                    next.endDate = addWorkingDays(next.startDate, neededDays, iw);
                }
                next.hoursPerDay = derivedHpd;
                next.totalHours = total;
            }
            return next;
        }

        // Case 3: hpd === 0, no dates → just store totalHours, wait for hpd
        return next;
    }

    // ── startDate changed ─────────────────────────────────────────────────
    if ("startDate" in patch) {
        const newStart = next.startDate;
        if (!newStart) {
            next.endDate = null;
            next.totalHours = 0;
            return next;
        }
        if (next.endDate && newStart > next.endDate) {
            next.endDate = null;
            next.totalHours = 0;
            return next;
        }
        if (hpd > 0) {
            if (next.endDate) {
                // Dates changed → recalc totalHours (user is adjusting the range)
                next.totalHours = roundTotal(hpd * countDays(newStart, next.endDate, iw));
            } else if ((next.totalHours ?? 0) > 0) {
                const days = Math.ceil(next.totalHours / hpd);
                next.endDate = addWorkingDays(newStart, days, iw);
                // Adjust hpd to fit totalHours exactly
                const actualDays = countDays(newStart, next.endDate, iw);
                next.hoursPerDay = actualDays > 0 ? round2(next.totalHours / actualDays) : hpd;
            }
        } else if ((next.totalHours ?? 0) > 0 && next.endDate) {
            // hpd=0, totalHours exists, both dates now available → distribute
            const days = countDays(newStart, next.endDate, iw);
            if (days > 0) {
                let derivedHpd = round2(next.totalHours / days);
                if (derivedHpd > MAX_HPD) {
                    derivedHpd = MAX_HPD;
                    const neededDays = Math.ceil(next.totalHours / MAX_HPD);
                    next.endDate = addWorkingDays(newStart, neededDays, iw);
                }
                next.hoursPerDay = derivedHpd;
            }
        }
        return next;
    }

    // ── endDate changed ───────────────────────────────────────────────────
    if ("endDate" in patch) {
        const newEnd = next.endDate;
        if (!newEnd) {
            next.totalHours = 0;
            return next;
        }
        if (next.startDate && newEnd < next.startDate) {
            next.endDate = null;
            next.totalHours = 0;
            return next;
        }
        if (hpd > 0 && next.startDate) {
            next.totalHours = roundTotal(hpd * countDays(next.startDate, newEnd, iw));
        } else if (hpd === 0 && (next.totalHours ?? 0) > 0 && next.startDate) {
            // hpd=0, totalHours exists, both dates now available → distribute
            const days = countDays(next.startDate, newEnd, iw);
            if (days > 0) {
                let derivedHpd = round2(next.totalHours / days);
                if (derivedHpd > MAX_HPD) {
                    derivedHpd = MAX_HPD;
                    const neededDays = Math.ceil(next.totalHours / MAX_HPD);
                    next.endDate = addWorkingDays(next.startDate, neededDays, iw);
                }
                next.hoursPerDay = derivedHpd;
            }
        }
        return next;
    }

    // ── isIncludeWeekends changed ─────────────────────────────────────────
    if ("isIncludeWeekends" in patch) {
        if (next.startDate && next.endDate) {
            const days = countDays(next.startDate, next.endDate, iw);
            if (hpd > 0) {
                next.totalHours = roundTotal(hpd * days);
            } else if ((next.totalHours ?? 0) > 0 && days > 0) {
                // Redistribute totalHours across new day count
                let derivedHpd = round2(next.totalHours / days);
                if (derivedHpd > MAX_HPD) {
                    derivedHpd = MAX_HPD;
                    const neededDays = Math.ceil(next.totalHours / MAX_HPD);
                    next.endDate = addWorkingDays(next.startDate, neededDays, iw);
                }
                next.hoursPerDay = derivedHpd;
            }
        }
        return next;
    }

    return next;
}

// ─── Sanitizer (only for buildPayload) ────────────────────────────────────────

function sanitizeAllocation(a) {
    const hpd = parseFloat(a.hoursPerDay) || 0;

    // hpd = 0 → zero total and dates, but keep hpd = 0
    if (hpd === 0) {
        return { ...a, hoursPerDay: 0, totalHours: 0, startDate: a.startDate, endDate: a.endDate };
    }

    const total = parseFloat(a.totalHours) || 0;
    const iw = a.isIncludeWeekends ?? false;
    let { startDate, endDate } = a;

    // endDate without startDate → clear both, zero total, keep hpd
    if (!startDate && endDate) {
        return { ...a, startDate: null, endDate: null, totalHours: 0 };
    }

    // hpd > 0, total > 0, no dates → derive both
    if (!startDate && !endDate && total > 0) {
        startDate = todayISO(iw);
        const days = Math.ceil(total / hpd);
        endDate = addWorkingDays(startDate, days, iw);
    }

    // hpd > 0, total > 0, startDate but no endDate → derive endDate
    if (startDate && !endDate && total > 0) {
        const days = Math.ceil(total / hpd);
        endDate = addWorkingDays(startDate, days, iw);
    }

    // Snap totalHours if both dates exist
    if (startDate && endDate) {
        const snapped = roundTotal(hpd * countDays(startDate, endDate, iw));
        return { ...a, startDate, endDate, totalHours: snapped };
    }

    // No dates and total = 0: that's fine, return as-is with hpd preserved
    return { ...a, startDate: startDate ?? null, endDate: endDate ?? null, totalHours: total };
}

// ─── Default allocation ───────────────────────────────────────────────────────

function defaultAllocation() {
    return {
        hoursPerDay: DEFAULT_HPD,
        totalHours: 0,
        startDate: null,
        endDate: null,
        isIncludeWeekends: false,
    };
}

// ─── Conflict icon helper ─────────────────────────────────────────────────────

function ConflictIcon({ type }) {
    if (type === "ResourceCapacity") {
        return <XCircle size={16} className={styles.conflictIconError} />;
    }
    return <AlertTriangle size={16} className={styles.conflictIconWarn} />;
}

function getConflictSeverity(conflicts) {
    if (!conflicts) return "warn";
    return conflicts.some(c => c.conflictType === "ResourceCapacity") ? "error" : "warn";
}

function formatConflictType(type) {
    if (!type) return "Conflict";
    return type.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function groupByConflictType(conflicts) {
    const groups = {};
    for (const c of conflicts) {
        const key = c.conflictType || "Other";
        if (!groups[key]) groups[key] = [];
        groups[key].push(c);
    }
    return groups;
}

// ─── Per-row conflict banner ──────────────────────────────────────────────────

function RowConflictBanner({ conflicts }) {
    if (!conflicts || conflicts.length === 0) return null;
    const severity = getConflictSeverity(conflicts);
    const bannerClass = severity === "error"
        ? `${styles.rowConflictBanner} ${styles.rowConflictBannerError}`
        : `${styles.rowConflictBanner} ${styles.rowConflictBannerWarn}`;
    const groups = groupByConflictType(conflicts);
    return (
        <div className={bannerClass}>
            {Object.entries(groups).map(([type, items]) => (
                <div key={type} className={styles.conflictGroup}>
                    <div className={styles.conflictGroupTitle}>
                        <ConflictIcon type={type} />
                        <span>{formatConflictType(type)}</span>
                    </div>
                    <ul className={styles.conflictList}>
                        {items.map((c, i) => (
                            <li key={i} className={styles.conflictItem}>
                                <SafeHtml html={c.message} className={styles.conflictMessage} />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

// ─── Hover popover ────────────────────────────────────────────────────────────

function AllocationHoverPopover({ allocation, user, children }) {
    const hasAllocation = allocation &&
        (allocation.hoursPerDay || allocation.startDate || allocation.endDate || allocation.totalHours);
    if (!hasAllocation) return <>{children}</>;

    const content = (
        <div className={styles.hoverPopoverContent}>
            <div className={styles.hoverPopoverHeader}>
                <Avatar color="colorful" name={user.name} image={{ src: user.image }} size={28} />
                <div>
                    <p className={styles.hoverPopoverName}>{user.name}</p>
                    {user.title && <p className={styles.hoverPopoverTitle}>{user.title}</p>}
                </div>
            </div>
            <div className={styles.hoverPopoverDivider} />
            <div className={styles.hoverPopoverRows}>
                <div className={styles.hoverPopoverRow}>
                    <Clock size={13} className={styles.hoverIcon} />
                    <span className={styles.hoverLabel}>Hours / day</span>
                    <span className={styles.hoverValue}>{allocation.hoursPerDay ?? "—"} h</span>
                </div>
                <div className={styles.hoverPopoverRow}>
                    <Clock size={13} className={styles.hoverIcon} />
                    <span className={styles.hoverLabel}>Total hours</span>
                    <span className={styles.hoverValue}>{allocation.totalHours ?? 0} h</span>
                </div>
                <div className={styles.hoverPopoverRow}>
                    <Calendar size={13} className={styles.hoverIcon} />
                    <span className={styles.hoverLabel}>Start date</span>
                    <span className={styles.hoverValue}>{allocation.startDate ?? "—"}</span>
                </div>
                <div className={styles.hoverPopoverRow}>
                    <Calendar size={13} className={styles.hoverIcon} />
                    <span className={styles.hoverLabel}>End date</span>
                    <span className={styles.hoverValue}>{allocation.endDate ?? "—"}</span>
                </div>
            </div>
        </div>
    );

    return (
        <Tooltip content={content} relationship="label" positioning="above-start" withArrow>
            <span>{children}</span>
        </Tooltip>
    );
}

// ─── Allocation row (with debounced number inputs) ────────────────────────────

function AllocationRow({ user, allocation, onPatch, onApplyToAll, showApplyToAll, isDirty, isValidating, onConfirm, onCancel, conflicts, disabled }) {
    // emit sends a raw patch to the parent — parent applies recalc in functional setState
    const emit = (patch) => onPatch(user.id, patch);

    // Local debounced state for number inputs
    const [localHpd, setLocalHpd] = useState(allocation.hoursPerDay ?? "");
    const [localTotal, setLocalTotal] = useState(allocation.totalHours ?? "");

    const hpdFocusRef = useRef(false);
    const totalFocusRef = useRef(false);
    const hpdTimerRef = useRef(null);
    const totalTimerRef = useRef(null);

    // Sync from props when not focused (e.g. applyToAll, date-driven recalcs)
    useEffect(() => {
        if (!hpdFocusRef.current) {
            const incoming = allocation.hoursPerDay ?? "";
            if (String(incoming) !== String(localHpd)) setLocalHpd(incoming);
        }
    }, [allocation.hoursPerDay]);

    useEffect(() => {
        if (!totalFocusRef.current) {
            const incoming = allocation.totalHours ?? "";
            if (String(incoming) !== String(localTotal)) setLocalTotal(incoming);
        }
    }, [allocation.totalHours]);

    // Cleanup timers
    useEffect(() => {
        return () => {
            if (hpdTimerRef.current) clearTimeout(hpdTimerRef.current);
            if (totalTimerRef.current) clearTimeout(totalTimerRef.current);
        };
    }, []);

    const flushHpd = (val) => {
        if (hpdTimerRef.current) clearTimeout(hpdTimerRef.current);
        const clamped = Math.min(Math.max(parseFloat(val) || 0, 0), MAX_HPD);
        emit({ hoursPerDay: clamped });
    };

    const handleHpdChange = (val) => {
        setLocalHpd(val);
        if (hpdTimerRef.current) clearTimeout(hpdTimerRef.current);
        hpdTimerRef.current = setTimeout(() => flushHpd(val), DEBOUNCE_MS);
    };

    const handleHpdBlur = () => {
        hpdFocusRef.current = false;
        flushHpd(localHpd);
    };

    const flushTotal = (val) => {
        if (totalTimerRef.current) clearTimeout(totalTimerRef.current);
        emit({ totalHours: parseFloat(val) || 0 });
    };

    const handleTotalChange = (val) => {
        setLocalTotal(val);
        if (totalTimerRef.current) clearTimeout(totalTimerRef.current);
        totalTimerRef.current = setTimeout(() => flushTotal(val), DEBOUNCE_MS);
    };

    const handleTotalBlur = () => {
        totalFocusRef.current = false;
        flushTotal(localTotal);
    };

    return (
        <>
            <RowConflictBanner conflicts={conflicts} />
            <div className={styles.allocationRow}>
                <div className={styles.allocationUserCell}>
                    <Avatar color="colorful" name={user.name} image={{ src: user.image }} size={26} />
                    <div className={styles.allocationUserName}>
                        <span className={styles.userName}>{user.name}</span>
                        {user.title && <span className={styles.userTitle}>{user.title}</span>}
                    </div>
                </div>

                {/* H / Day — debounced */}
                <div className={styles.allocationNumCell}>
                    <Input
                        icon={false} type="number" placeholder="8"
                        value={localHpd}
                        handleChange={handleHpdChange}
                        onFocus={() => { hpdFocusRef.current = true; }}
                        onBlur={handleHpdBlur}
                        label=""
                        rightStaticText="h/day"
                    />
                </div>

                {/* Total hours — debounced */}
                <div className={styles.allocationNumCell}>
                    <Input
                        icon={false} type="number" placeholder="0"
                        value={localTotal}
                        handleChange={handleTotalChange}
                        onFocus={() => { totalFocusRef.current = true; }}
                        onBlur={handleTotalBlur}
                        label=""
                    />
                </div>

                {/* Start date — instant */}
                <div className={styles.allocationDateCell}>
                    <Input
                        icon={false}
                        type="date"
                        disabelWeekends
                        value={allocation.startDate ?? ""}
                        maxDate={allocation.endDate ?? undefined}
                        handleChange={(val) => emit({ startDate: val || null })}
                        label=""
                    />
                </div>

                {/* End date — instant */}
                <div className={styles.allocationDateCell}>
                    <Input
                        icon={false}
                        type="date"
                        disabelWeekends
                        value={allocation.endDate ?? ""}
                        minDate={allocation.startDate ?? undefined}
                        handleChange={(val) => emit({ endDate: val || null })}
                        label=""
                    />
                </div>

                {/* Confirm / Cancel */}
                {!disabled && (
                    <div className={styles.rowActions}>
                        {isValidating ? (
                            <Spin label="" />
                        ) : isDirty &&
                        <>
                            <Tooltip content="Confirm allocation" relationship="label">
                                <button className={styles.confirmBtn} onClick={() => onConfirm(user.id)} type="button">
                                    <Check size={14} />
                                </button>
                            </Tooltip>
                            <Tooltip content="Cancel changes" relationship="label">
                                <button className={styles.cancelBtn} onClick={() => onCancel(user.id)} type="button">
                                    <X size={14} />
                                </button>
                            </Tooltip>
                        </>
                        }
                        {/* Apply to all */}
                        {!disabled && !isValidating && showApplyToAll && (
                            <Tooltip content="Apply to all resources" relationship="label">
                                <button className={styles.applyToAllBtn} onClick={() => onApplyToAll(allocation)} type="button">
                                    <Copy size={12} />
                                    <span>all</span>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}


            </div>
        </>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaskResourceAllocationManager({
    scopeId, taskId = null, allocations = [],
    onChange, onTaskDateConflicts, loading = false, disabled = false,
    label = "", buttonLabel = "Assign Resources", searchPlaceholder = "Search",
    taskStartDate = null, taskEndDate = null,
}) {
    const dispatch = useDispatch();
    const searchTimeoutRef = useRef(null);
    const selfUpdateRef = useRef(false);
    const pageSize = 10;

    const [openPopover, setOpenPopover] = useState(false);
    const [users, setUsers] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [allocationMap, setAllocationMap] = useState({});
    const [confirmedMap, setConfirmedMap] = useState({});
    const [conflictsMap, setConflictsMap] = useState({});
    const [validatingMap, setValidatingMap] = useState({});
    const [showAllocationPanel, setShowAllocationPanel] = useState(false);

    // Init from allocations (edit mode) — skip when the component itself triggered the update
    useEffect(() => {
        if (selfUpdateRef.current) { selfUpdateRef.current = false; return; }
        if (allocations && allocations.length > 0) {
            const { map, confirmedMap } = allocations.reduce((acc, a) => {
                const isDirty = a.isDirty || false;

                const allocation = {
                    hoursPerDay: a.hoursPerDay ?? null,
                    totalHours: a.totalHours ?? 0,
                    startDate: a.startDate ? moment(a.startDate).format("YYYY-MM-DD") : null,
                    endDate: a.endDate ? moment(a.endDate).format("YYYY-MM-DD") : null,
                    isIncludeWeekends: a.isIncludeWeekends ?? false,
                    isDirty,
                };

                acc.map[a.resource.id] = allocation;

                if (!isDirty) {
                    acc.confirmedMap[a.resource.id] = allocation;
                }

                return acc;
            }, { map: {}, confirmedMap: {} });

            setSelectedUsers(allocations.map(a => a.resource));
            setAllocationMap(map);
            setConfirmedMap(confirmedMap);
            setConflictsMap({});
        }
    }, [allocations]);

    // Seed unconfirmed allocations with task dates (implicit link).
    // Keeps syncing until the user confirms — uses confirmedMap to decide.
    useEffect(() => {
        if (!taskStartDate || !taskEndDate) return;
        setAllocationMap((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const userId of Object.keys(next)) {
                const confirmed = confirmedMap[userId];
                // Only seed if the confirmed snapshot has no dates (never confirmed with dates)
                if (confirmed && !confirmed.startDate && !confirmed.endDate) {
                    next[userId] = recalc({ startDate: taskStartDate, endDate: taskEndDate }, next[userId]);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [taskStartDate, taskEndDate, confirmedMap]);

    const filterUsers = (results, selected) => {
        const ids = new Set(selected.map((u) => u.id));
        return results.filter((u) => !ids.has(u.id));
    };

    const fetchUsers = useCallback(
        (search = "", page = 1, append = false) => {
            if (!scopeId) { setUsers([]); setIsSearchLoading(false); setIsLoadingMore(false); return; }
            if (!append) { setIsSearchLoading(true); setUsers([]); } else { setIsLoadingMore(true); }
            searchService.Users(search, { pageSize, pageNumber: page, scopeId })
                .then((response) => {
                    const pagination = response.headers?.["x-pagination"]
                        ? JSON.parse(response.headers["x-pagination"]) : null;
                    const filtered = filterUsers(response.data || [], selectedUsers);
                    setUsers((prev) => append ? [...prev, ...filtered] : filtered);
                    setHasMore(pagination?.HasNext || false);
                    setIsSearchLoading(false);
                    setIsLoadingMore(false);
                })
                .catch(() => {
                    setUsers([]);
                    setIsSearchLoading(false);
                    setIsLoadingMore(false);
                    dispatch(generalActions.showToast({ title: "Something went wrong.", type: "error" }));
                });
        },
        [selectedUsers, scopeId]
    );

    const debouncedSearch = (value) => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => { setPageNumber(1); fetchUsers(value, 1, false); }, 500);
    };

    const handleSearchChange = (value) => { setSearchTerm(value); debouncedSearch(value); };
    const handleOpenPopover = () => { setSearchTerm(""); setPageNumber(1); fetchUsers("", 1, false); setOpenPopover(true); };
    const handleLoadMore = () => { const next = pageNumber + 1; setPageNumber(next); fetchUsers(searchTerm, next, true); };

    const handleAddUser = (user) => {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
        const updatedUsers = [...selectedUsers, user];
        setSelectedUsers(updatedUsers);
        let alloc = defaultAllocation();
        const seeded = taskStartDate && taskEndDate
            ? recalc({ startDate: taskStartDate, endDate: taskEndDate }, alloc)
            : null;
        const newMap = {
            ...allocationMap,
            [user.id]: seeded || alloc,
        };
        setAllocationMap(newMap);
        const newConfirmed = { ...confirmedMap, [user.id]: { ...alloc } };
        setConfirmedMap(newConfirmed);
        selfUpdateRef.current = true;
        onChange(buildPayload(updatedUsers, newConfirmed));
    };

    const handleRemoveUser = (user) => {
        const updated = selectedUsers.filter((u) => u.id !== user.id);
        setSelectedUsers(updated);
        setUsers((prev) => [...prev, user]);
        const newMap = { ...allocationMap };
        delete newMap[user.id];
        setAllocationMap(newMap);
        const newConfirmed = { ...confirmedMap };
        delete newConfirmed[user.id];
        setConfirmedMap(newConfirmed);
        const newConflicts = { ...conflictsMap };
        delete newConflicts[user.id];
        setConflictsMap(newConflicts);
        if (updated.length === 0) setShowAllocationPanel(false);
        selfUpdateRef.current = true;
        onChange(buildPayload(updated, newConfirmed));
    };

    // Receives a raw patch from AllocationRow — applies recalc in functional setState
    // so it always reads the latest state (no stale closures).
    const handleAllocationPatch = (userId, patch) => {
        setAllocationMap((prev) => ({
            ...prev,
            [userId]: recalc(patch, prev[userId] || {}),
        }));
    };

    const handleApplyToAll = (sourceAllocation) => {
        const next = {};
        selectedUsers.forEach((u) => { next[u.id] = { ...sourceAllocation }; });
        setAllocationMap(next);
    };

    const isDirty = (userId) => {
        const current = allocationMap[userId];
        const confirmed = confirmedMap[userId];
        if (!current || !confirmed) return true;
        return current.hoursPerDay !== confirmed.hoursPerDay
            || current.totalHours !== confirmed.totalHours
            || current.startDate !== confirmed.startDate
            || current.endDate !== confirmed.endDate
            || current.isIncludeWeekends !== confirmed.isIncludeWeekends;
    };

    const handleConfirm = (userId) => {
        const alloc = sanitizeAllocation(allocationMap[userId] || {});
        const payload = {
            resourceId: userId,
            startDate: alloc.startDate,
            endDate: alloc.endDate,
            hoursPerDay: alloc.hoursPerDay,
            isIncludeWeekends: alloc.isIncludeWeekends,
        };

        setValidatingMap((prev) => ({ ...prev, [userId]: true }));

        const apiCall = taskId
            ? scopeService.validateAllocation({ taskId, payload })
            : scopeService.validateNewAllocations({ payload: [payload] });

        apiCall
            .then((response) => {
                const result = response?.data ?? response;
                if (result && result.isPermissible === false) {
                    setConflictsMap((prev) => ({ ...prev, [userId]: result.conflicts || [] }));
                    return;
                }
                // Permissible — promote to confirmed, clear conflicts
                setConflictsMap((prev) => ({ ...prev, [userId]: [] }));
                const newConfirmed = { ...confirmedMap, [userId]: { ...alloc } };
                setConfirmedMap(newConfirmed);
                selfUpdateRef.current = true;
                onChange(buildPayload(selectedUsers, newConfirmed));

                // Recalculate task dates from all confirmed allocations and validate
                validateTaskDatesFromAllocations(newConfirmed);
            })
            .catch(() => {
                dispatch(generalActions.showToast({ title: "Validation failed. Please try again.", type: "error" }));
            })
            .finally(() => {
                setValidatingMap((prev) => ({ ...prev, [userId]: false }));
            });
    };

    const validateTaskDatesFromAllocations = (confirmed) => {
        const confirmedAllcoations = Object.entries(confirmed).map(([resourceId, a]) => ({
            resourceId,
            startDate: a.startDate || null,
            endDate: a.endDate || null,
            hoursPerDay: a.hoursPerDay ?? 0,
            isIncludeWeekends: a.isIncludeWeekends ?? false
        }));
        const dates = Object.values(confirmed)
            .filter((a) => a.startDate && a.endDate)
            .map((a) => ({ start: a.startDate, end: a.endDate }));

        if (dates.length === 0) return;

        const minStart = dates.reduce((min, d) => (d.start < min ? d.start : min), dates[0].start);
        const maxEnd = dates.reduce((max, d) => (d.end > max ? d.end : max), dates[0].end);

        if (!taskId) {
            // Create mode — just notify parent of computed dates, no API call
            if (onTaskDateConflicts) onTaskDateConflicts([]);
            return;
        }

        scopeService.validateTaskDates(
            { taskId, 
              payload: { 
                startDate: minStart, 
                endDate: maxEnd, 
                pendingAllocations: confirmedAllcoations 
            }})
            .then((response) => {
                const result = response?.data ?? response;
                if (result && result.isPermissible === false) {
                    if (onTaskDateConflicts) onTaskDateConflicts(result.conflicts || []);
                } else {
                    if (onTaskDateConflicts) onTaskDateConflicts([]);
                }
            })
            .catch(() => {
                // Don't block the flow — just clear conflicts
                if (onTaskDateConflicts) onTaskDateConflicts([]);
            });
    };

    const handleCancel = (userId) => {
        const original = confirmedMap[userId];
        if (original) {
            setAllocationMap((prev) => ({ ...prev, [userId]: { ...original } }));
        }
        setConflictsMap((prev) => ({ ...prev, [userId]: [] }));
    };

    const buildPayload = (users, map) =>
        users.map((u) => {
            const raw = map[u.id] || {};
            const a = sanitizeAllocation(raw);
            return {
                resource: u,
                resourceId: u.id,
                hoursPerDay: a.hoursPerDay ?? null,
                totalHours: a.totalHours ?? 0,
                startDate: a.startDate ?? null,
                endDate: a.endDate ?? null,
                isIncludeWeekends: a.isIncludeWeekends ?? false,
            };
        });

    return (
        <div className={styles.wrapper}>
            {label && <label className={styles.label}>{label}</label>}
            {loading ? (
                <Skeleton>
                    <SkeletonItem style={{ width: "100%", height: "48px", borderRadius: "6px" }} />
                </Skeleton>
            ) : (
                <>
                    <div className={styles.selectedBar}>
                        <TeachingPopover positioning="below-start" withArrow={false} open={openPopover}
                            onOpenChange={(_, data) => !disabled && setOpenPopover(data.open)}>
                            <TeachingPopoverTrigger>
                                <Button className={styles.assignAvatarBtn} onClick={handleOpenPopover} disabled={disabled}>
                                    <Avatar icon={<AddPersonIcon color={"#342c82"} />} name="" color="transparent" />
                                    <p className={`${styles.buttonLabel} text-xsm-400 ms-1 d-flex align-items-center`}>{buttonLabel}</p>
                                </Button>
                            </TeachingPopoverTrigger>
                            <TeachingPopoverSurface className={styles.assignAvatarSurface}>
                                <TeachingPopoverBody className={styles.assignAvatarBody}>
                                    <Input placeholder={searchPlaceholder} handleChange={handleSearchChange} />
                                    {isSearchLoading && users.length === 0 ? (
                                        <div className="d-flex justify-content-center mt-4"><Spin /></div>
                                    ) : users.length > 0 ? (
                                        <div className={`${styles.userListWrapper} mt-2`}>
                                            {users.map((user) => (
                                                <div key={user.id} className={`${styles.userRow} cursor-pointer`} onClick={() => handleAddUser(user)}>
                                                    <Avatar color="colorful" name={user.name} image={{ src: user.image }} size={26} />
                                                    <p className="text-xsm-400">{user.name}</p>
                                                </div>
                                            ))}
                                            {hasMore && (
                                                <div className={styles.loadMoreRow}>
                                                    {isLoadingMore ? <Spin /> : searchTerm
                                                        ? <Button onClick={handleLoadMore} className={styles.loadMoreBtn}>Load more</Button>
                                                        : null}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={styles.noResults}>
                                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18.75 4A3.25 3.25 0 0 1 22 7.25v9.505a3.25 3.25 0 0 1-3.25 3.25H5.25A3.25 3.25 0 0 1 2 16.755V7.25a3.25 3.25 0 0 1 3.066-3.245L5.25 4h13.5Zm-9.497 8.5H5.75a.75.75 0 0 0-.743.648L5 13.25v.581c0 1.273.968 1.919 2.501 1.919 1.466 0 2.416-.59 2.496-1.755l.006-.163v-.582a.75.75 0 0 0-.649-.743l-.101-.007Zm2.997 0-1.416-.001c.086.182.142.381.161.591l.008.16v.582l-.01.254a2.85 2.85 0 0 1-.205.899c.108.01.22.015.337.015 1.052 0 1.793-.407 1.869-1.27l.006-.147v-.333a.75.75 0 0 0-.648-.743l-.102-.007-1.416-.001 1.416.001Zm6 0h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486l-.102-.007ZM7.5 8.5a1.5 1.5 0 1 0 0 3.001 1.5 1.5 0 0 0 0-3.001Zm3.87.502a1.248 1.248 0 1 0 0 2.496 1.248 1.248 0 0 0 0-2.496Zm6.88.498h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486L18.25 9.5Z" fill="#342C82" />
                                            </svg>
                                            <h3 className={styles.noResultsTitle}>
                                                {searchTerm ? "No resource found" : "Start typing to search users"}
                                            </h3>
                                        </div>
                                    )}
                                </TeachingPopoverBody>
                            </TeachingPopoverSurface>
                        </TeachingPopover>

                        {selectedUsers.map((user) => (
                            <AllocationHoverPopover key={user.id} allocation={allocationMap[user.id]} user={user}>
                                <div className={styles.userChip}>
                                    <Avatar color="colorful" name={user.name} image={{ src: user.image }} size={22} />
                                    <span className={styles.chipName}>{user.name}</span>
                                    <span className={styles.chipHours}>{allocationMap[user.id]?.totalHours ?? 0} h</span>
                                    {!disabled && (
                                        <button className={styles.chipRemove} onClick={() => handleRemoveUser(user)} type="button">
                                            <FilterCross />
                                        </button>
                                    )}
                                </div>
                            </AllocationHoverPopover>
                        ))}

                        {selectedUsers.length > 0 && !disabled && (
                            <button type="button" className={styles.manageAllocBtn} onClick={() => setShowAllocationPanel((v) => !v)}>
                                {showAllocationPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                Manage Allocation
                            </button>
                        )}
                    </div>

                    {showAllocationPanel && selectedUsers.length > 0 && (
                        <div className={styles.allocationPanel}>
                            <div className={styles.allocationPanelHeader}>
                                <span className={styles.panelTitle}>Resource allocation</span>
                            </div>
                            <div className={styles.allocationColumnHeaders}>
                                <div className={styles.colHeaderUser}></div>
                                <div className={styles.colHeaderCell}></div>
                                <div className={styles.colHeaderCell}>total hours</div>
                                <div className={styles.colHeaderCell}>start date</div>
                                <div className={styles.colHeaderCell}>end date</div>
                                {selectedUsers.length > 1 && <div />}
                            </div>
                            {selectedUsers.map((user) => (
                                <AllocationRow
                                    key={user.id}
                                    user={user}
                                    allocation={allocationMap[user.id] || {}}
                                    onPatch={handleAllocationPatch}
                                    onApplyToAll={handleApplyToAll}
                                    showApplyToAll={selectedUsers.length > 1}
                                    isDirty={isDirty(user.id)}
                                    isValidating={validatingMap[user.id] || false}
                                    onConfirm={handleConfirm}
                                    onCancel={handleCancel}
                                    conflicts={conflictsMap[user.id] || []}
                                    disabled={disabled}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}